import { lazy } from "react";
import { UserRole } from "../types/user";

// Lazy load all page components
const Login = lazy(() => import("../pages/Login"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const DashboardDetails = lazy(() => import("../pages/DashboardDetails"));
const Surveys = lazy(() => import("../pages/Surveys"));
const Support = lazy(() => import("../pages/Support"));
const Callback = lazy(() => import("../pages/Callback"));
const VerifyEmail = lazy(() => import("../pages/VerifyEmail"));
const Datasets = lazy(() => import("../pages/Datasets"));
const DatasetDetails = lazy(() => import("../pages/DatasetDetails"));
const Settings = lazy(() => import("../pages/Settings"));
const ConfigurationDetails = lazy(
  () => import("../pages/ConfigurationDetails"),
);
const SurveyHistory = lazy(() => import("../pages/SurveyHistory"));

// Route configuration with metadata
export interface RouteConfig {
  path: string;
  component: React.LazyExoticComponent<React.ComponentType<any>>;
  loadingMessage: string;
  protected?: boolean;
  layout?: boolean;
  allowUnverified?: boolean; // Allow access even if email is not verified
  requiredRole?:
    | import("../types/user").UserRole
    | import("../types/user").UserRole[];
  requiredPermission?: import("../types/user").Permission;
  allowedRoles?: import("../types/user").UserRole[];
}

export const routeConfigs: RouteConfig[] = [
  {
    path: "/login",
    component: Login,
    loadingMessage: "Loading login...",
    protected: false,
  },
  {
    path: "/callback",
    component: Callback,
    loadingMessage: "Processing authentication...",
    protected: false,
  },
  {
    path: "/verify-email",
    component: VerifyEmail,
    loadingMessage: "Loading verification...",
    protected: false,
    allowUnverified: true,
  },
  {
    path: "/dashboard",
    component: Dashboard,
    loadingMessage: "Loading dashboard...",
    protected: true,
    layout: true,
  },
  {
    path: "/dashboard/:slug",
    component: DashboardDetails,
    loadingMessage: "Loading dashboard details...",
    protected: true,
    layout: true,
  },
  {
    path: "/surveys",
    component: Surveys,
    loadingMessage: "Loading surveys...",
    protected: true,
    layout: true,
    allowedRoles: [UserRole.ADMIN, UserRole.RESEARCHER],
  },
  {
    path: "/datasets",
    component: Datasets,
    loadingMessage: "Loading datasets...",
    protected: true,
    layout: true,
    allowedRoles: [UserRole.ADMIN, UserRole.RESEARCHER],
  },
  {
    path: "/datasets/:id",
    component: DatasetDetails,
    loadingMessage: "Loading dataset details...",
    protected: true,
    layout: true,
    allowedRoles: [UserRole.ADMIN, UserRole.RESEARCHER],
  },
  {
    path: "/settings",
    component: Settings,
    loadingMessage: "Loading settings...",
    protected: true,
    layout: true,
    allowedRoles: [UserRole.ADMIN, UserRole.RESEARCHER],
  },
  {
    path: "/datasets/:id/config",
    component: ConfigurationDetails,
    loadingMessage: "Loading configuration details...",
    protected: true,
    layout: true,
    allowedRoles: [UserRole.ADMIN, UserRole.RESEARCHER],
  },
  {
    path: "/support",
    component: Support,
    loadingMessage: "Loading support...",
    protected: true,
    layout: true,
    allowedRoles: [UserRole.ADMIN, UserRole.RESEARCHER],
  },
  {
    path: "/surveys/:surveyId/history",
    component: SurveyHistory,
    loadingMessage: "Loading survey history...",
    protected: true,
    layout: true,
    allowedRoles: [UserRole.ADMIN, UserRole.RESEARCHER],
  },
];

export default routeConfigs;
